// src/hooks/useUsersData.js

import { useEffect, useState } from "react";
import config from "../config";

/**
 * useUsersData
 *
 * Purpose:
 *   Fetches the list of users/companies (and their locations) for the
 *   Receiver / Material Management page.
 *
 
 */
function getCsrfToken() {
  // Try meta-tag first (common in many frameworks)
  const meta = typeof document !== "undefined" && document.querySelector('meta[name="csrf-token"]');
  const metaToken = meta?.getAttribute("content");
  if (metaToken) return metaToken;

  const match =
    typeof document !== "undefined" &&
    document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith("csrf_token="));

  if (match) {
    const value = match.substring("csrf_token=".length);
    return decodeURIComponent(value);
  }

  return "";
}

export const useUsersData = (companyId = null) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    /**
     * AbortController is the preferred modern pattern for cancelling fetches.
     * It prevents setting state after unmount and avoids the "cancelled flag" pattern.
     */
    const controller = new AbortController();

    const fetchUsers = async () => {
      setLoading(true);
      setErrorMessage("");

      /**
       * Encode query parameter defensively.
       * This prevents malformed URLs and avoids weird parsing edge-cases.
       *
       */
      const queryParam =
        companyId != null && companyId !== ""
          ? `?company_id=${encodeURIComponent(String(companyId))}`
          : "";

      // Build URL once for readability and to avoid accidental string mistakes.
      const url = `${config.apiUrl}/material_management/users${queryParam}`;

      try {
        /**
         * Cookie-based auth only:
         * - We deliberately do NOT read tokens from localStorage.
         * - We deliberately do NOT attach Authorization: Bearer ...
         *
         * This removes the "XSS steals access_token from localStorage" risk,
         * and eliminates mixed-auth ambiguity (cookies + bearer).
         */
        const csrfToken = getCsrfToken();

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",

            ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
          },

          credentials: "include",

          /**
           * Wire abort signal so the request can be cancelled on unmount.
           */
          signal: controller.signal,
        });

        /**
         * Attempt to parse JSON.
         * If the server returns non-JSON (misconfigured endpoint / proxy error),
         * JSON parsing might throw and will be handled below.
         */
        const data = await response.json();

        /**
         * Handle non-2xx responses with user-safe messaging.
         */
        if (!response.ok) {
          if (response.status === 401) {
            // User-safe: don't reveal token/cookie internals; just say they need to re-auth.
            throw new Error("Unauthorized. Please sign in again.");
          }

          // Log full details (developer visibility) but don't surface raw message to UI.
          console.error("Users endpoint returned error:", {
            status: response.status,
            statusText: response.statusText,
            body: data,
          });

          throw new Error("Unable to load users. Please try again later.");
        }

        /**
         * Normalize response shape:
         * - some backends return an array directly
         * - others return { users: [...] }
         */
        const usersArray = Array.isArray(data) ? data : Array.isArray(data?.users) ? data.users : [];

        // Update state with normalized array.
        setUsers(usersArray);

        /**
         * Avoid logging potentially sensitive PII/location info in production.
         */

      } catch (err) {
        /**
         * Abort is not an error state from the user's perspective.
         * It happens during unmount or dependency change.
         */
        if (err?.name === "AbortError") return;

        // Log real error details for developers.
        console.error("Error loading material management users:", err);

        /**
         * Show a generic, user-safe error message (avoid leaking internals).
         */
        setErrorMessage("Unable to load users for material management. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();

    /**
     * Cleanup: abort any in-flight request when:
     * - the component unmounts
     * - companyId changes and this effect re-runs
     */
    return () => {
      controller.abort();
    };
  }, [companyId]);

  return { users, loading, errorMessage };
};